/**
 * Small presentational primitives.
 *
 * Grouped in one file on purpose: each is a handful of lines, and eight separate
 * files of five lines each is harder to navigate than one file you can read top to
 * bottom. Anything that grows real behaviour (Dialog, Select, Table) gets its own
 * file.
 */
import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";
import { AlertCircle, ChevronRight, Inbox, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------- Card

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-card)] border border-subtle bg-card shadow-[var(--shadow-card)]",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start justify-between gap-3 border-b border-subtle px-4 py-3",
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-content">{title}</h2>
        {description !== undefined && (
          <p className="mt-0.5 text-sm text-content-secondary">{description}</p>
        )}
      </div>
      {actions !== undefined && <div className="flex shrink-0 gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4", className)} {...props} />;
}

// ---------------------------------------------------------------- Badge

/**
 * Badge tones.
 *
 * Four of these are STATUS: success, warning, danger, info. `accent` is not — it
 * marks the one row that is special ("Default", "Leave default", "Required from
 * you") and deliberately carries no outcome meaning.
 *
 * There is no `brand` tone, and that is deliberate. The brand green measures
 * dE 4.3 against `success` (the separability floor is 8), so a brand badge beside
 * an Approved badge was two different meanings in one colour. The brand green now
 * appears only as a SOLID FILL with white text — buttons — which is a different
 * treatment from every badge here and therefore never ambiguous. See the note on
 * Button's `primary` variant.
 */
export type BadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "accent";

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "bg-sunken text-content-secondary",
  success: "bg-success-surface text-success dark:bg-success/15 dark:text-success-surface",
  warning: "bg-warning-surface text-warning dark:bg-warning/15 dark:text-warning-surface",
  danger: "bg-danger-surface text-danger dark:bg-danger/15 dark:text-danger-surface",
  info: "bg-info-surface text-info dark:bg-info/15 dark:text-info-surface",
  // accent-700 on accent-50 is 5.90:1; accent-300 on the dark tint is 9.36:1.
  // Neither uses accent-400 as a text colour, which fails on light surfaces.
  accent: "bg-accent-50 text-accent-700 dark:bg-accent-400/15 dark:text-accent-300",
};

export function Badge({
  tone = "neutral",
  children,
  className,
  dot = false,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  /**
   * Adds a coloured dot. Worth using whenever the badge encodes STATUS: colour
   * alone fails for the ~8% of men with a colour vision deficiency, so the dot
   * plus the text label carries the meaning and the colour merely reinforces it.
   */
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        // ring-current/15 draws a crisp same-hue edge for every tone at once,
        // since each tone sets its text colour.
        "inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-current/15",
        BADGE_TONES[tone],
        className,
      )}
    >
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- states

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2">
      <Loader2 aria-hidden className={cn("size-4 animate-spin", className)} />
      <span className="sr-only">{label ?? "Loading"}</span>
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-lg border border-subtle bg-sunken text-content-muted [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden className="size-5" />}
      </div>
      <p className="text-sm font-medium text-content">{title}</p>
      {description !== undefined && (
        <p className="mt-1 max-w-sm text-sm text-content-secondary">{description}</p>
      )}
      {action !== undefined && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Could not load this",
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-lg border border-subtle bg-sunken">
        <AlertCircle aria-hidden className="size-5 text-danger" />
      </div>
      <p className="text-sm font-medium text-content">{title}</p>
      {description !== undefined && (
        <p className="mt-1 max-w-md text-sm text-content-secondary">{description}</p>
      )}
      {action !== undefined && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * A shimmering placeholder.
 *
 * `aria-hidden` because a screen reader announcing "loading loading loading" for
 * each of twelve skeleton rows is worse than silence; the region's own
 * `aria-busy`/`role="status"` carries that information once.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("animate-pulse rounded bg-[var(--surface-sunken)]", className)}
    />
  );
}

export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-busy className="divide-y divide-[var(--line-subtle)]">
      <span className="sr-only">Loading results</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-4", columnIndex === 0 ? "w-1/4" : "flex-1")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- layout

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb}
        <h1 className="text-lg font-semibold tracking-tight text-content sm:text-xl">
          {title}
        </h1>
        {description !== undefined && (
          <p className="mt-1 text-sm text-content-secondary">{description}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>
      )}
    </div>
  );
}

/** A labelled value, for detail panels. */
export function DescriptionItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="mt-0.5 truncate text-sm text-content">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------- breadcrumbs

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * For DETAIL pages only (a request, a task, a form being filled) — list pages
 * are already located by the sidebar, so a breadcrumb there is noise. Passed
 * through PageHeader's `breadcrumb` slot.
 */
export function Breadcrumbs({ items }: { items: readonly Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-1">
      <ol className="flex flex-wrap items-center gap-1 text-xs text-content-muted">
        {items.map((crumb, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${crumb.label}-${String(index)}`} className="flex items-center gap-1">
              {index > 0 && <ChevronRight aria-hidden className="size-3" />}
              {crumb.to !== undefined && !isLast ? (
                <Link to={crumb.to} className="transition-colors hover:text-content">
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? "page" : undefined}
                  className={cn(isLast && "font-medium text-content-secondary")}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
